import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { apiPost } from '../../../api';
import { loginStart, loginSuccess, loginFailure } from '../../../slices/authSlice';
import AppSwitcher from '../../../components/AppSwitcher';
import TwoFactorStep from '../../../components/TwoFactorStep';

const Web3Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 2FA step: login now returns a tempToken instead of logging in directly.
  const [twoFA, setTwoFA] = useState(null); // { method, tempToken }
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(loginStart());
    try {
      const res = await apiPost('/api/admin/auth/login', { email, password });
      if (res.data.twoFARequired) {
        setCode('');
        setTwoFA({ method: res.data.method, tempToken: res.data.tempToken });
        dispatch(loginFailure(null)); // clear loading, no error
      } else {
        dispatch(loginSuccess(res.data)); // fallback: direct login (no 2FA)
        navigate('/web3/hyperliquid/fills');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed';
      dispatch(loginFailure(msg));
    }
  };

  const handleVerify = async (value = code) => {
    if (value.length !== 6) return;
    dispatch(loginStart());
    try {
      const url =
        twoFA.method === 'email'
          ? '/api/admin/auth/login/email-2fa-verify'
          : '/api/admin/auth/login/2fa-verify';
      const res = await apiPost(url, { code: value, tempToken: twoFA.tempToken });
      dispatch(loginSuccess(res.data));
      navigate('/web3/hyperliquid/fills');
    } catch (err) {
      dispatch(loginFailure(err.response?.data?.message || 'Invalid or expired code'));
      setCode('');
    }
  };

  const backToLogin = () => {
    setTwoFA(null);
    setCode('');
    dispatch(loginFailure(null));
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute top-6 right-6 z-50">
        <AppSwitcher />
      </div>

      <div className="w-full max-w-sm">
        {/* Logo + title */}
        <div className="mb-8 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-ginox-green/30 bg-ginox-green/10 mb-5">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Ginox"
              className="h-8 w-8 object-contain"
            />
          </div>
          <div className="flex items-center justify-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
            Gidex
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
          </div>
          <h1 className="font-serif italic text-[32px] leading-[1.05] text-foreground">
            Gidex <span className="text-gradient-ginox">Admin</span>
          </h1>
          <p className="mt-1 text-xs text-muted-foreground font-mono tracking-[0.06em]">
            {twoFA ? 'Two-factor verification' : 'Sign in to your account'}
          </p>
        </div>

        {/* Form card */}
        <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl">
          {twoFA ? (
            <TwoFactorStep
              method={twoFA.method}
              email={email}
              code={code}
              onCodeChange={setCode}
              onComplete={handleVerify}
              onVerify={() => handleVerify()}
              onBack={backToLogin}
              loading={loading}
              error={error}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-ginox-green px-4 py-2.5 text-sm font-semibold text-black hover:bg-ginox-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Web3Login;
